import { useState, useMemo, useCallback } from 'react';

/**
 * Hook de paginación reutilizable con dos modos de operación.
 *
 * **Client-side** (`mode: 'client'`): recibe el array completo de datos y los
 * pagina localmente con `useMemo`. Ideal para datasets acotados donde se carga
 * todo de una vez. El total se deriva automáticamente de `data.length`.
 *
 * **Server-side** (`mode: 'server'`): gestiona el estado `page`/`limit` para
 * pasarlo a una función de fetch externa. El total debe inyectarse con `setTotal`
 * desde la respuesta del servidor.
 *
 * @param {Object}            options
 * @param {'client'|'server'} options.mode           - Modo de paginación
 * @param {Array}             [options.data=[]]       - (client) Datos completos a paginar
 * @param {number}            [options.initialPage=1]
 * @param {number}            [options.initialLimit=20]
 *
 * @returns {Object}
 * @returns {number}          returns.page
 * @returns {number}          returns.limit
 * @returns {number}          returns.totalItems       - data.length (client) | total inyectado (server)
 * @returns {Array|undefined} returns.paginatedData    - Slice actual (client) | undefined (server)
 * @returns {Function}        returns.setTotal         - Inyecta el total desde la respuesta (server)
 * @returns {Function}        returns.handlePageChange - (newPage: number) => void
 * @returns {Function}        returns.handleLimitChange - (newLimit: number) => void; resetea a página 1
 * @returns {Function}        returns.resetPage        - Vuelve a página 1
 */
function usePagination({ mode, data = [], initialPage = 1, initialLimit = 20 }) {
  const [page, setPage]   = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [total, setTotal] = useState(0);

  const paginatedData = useMemo(() => {
    if (mode !== 'client') return undefined;
    const start = (page - 1) * limit;
    return data.slice(start, start + limit);
  }, [mode, data, page, limit]);

  const totalItems = mode === 'client' ? data.length : total;

  // useCallback garantiza referencias estables para usarlas como deps en hooks consumidores
  const handlePageChange  = useCallback((newPage)  => setPage(newPage), []);
  const handleLimitChange = useCallback((newLimit) => { setLimit(newLimit); setPage(1); }, []);
  const resetPage         = useCallback(() => setPage(1), []);

  return {
    page,
    limit,
    totalItems,
    paginatedData,
    setTotal,
    handlePageChange,
    handleLimitChange,
    resetPage,
  };
}

export default usePagination;
